import { supabase } from '../../lib/supabase';

/**
 * Logs a system error to the `system_error_logs` table for production monitoring.
 * This is non-blocking: if the insert fails, we silently ignore it to avoid
 * cascading errors.
 */
export async function logSystemError(error: {
  module: string;
  page: string;
  error_type: string;
  error_message: string;
  user_role?: string;
  company_id?: string;
}): Promise<void> {
  try {
    await supabase.from('system_error_logs').insert({
      ...error,
      created_at: new Date().toISOString(),
    });
  } catch {
    // Non-blocking — do not let monitoring failures propagate
  }
}

/**
 * Detects whether a Supabase error is an RLS (Row Level Security) denial.
 */
export function isRLSError(error: unknown): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : (error as { message?: string }).message ?? '';
  return (
    msg.includes('new row violates row-level security') ||
    msg.includes('permission denied') ||
    msg.includes('RLS') ||
    msg.includes('row-level security')
  );
}

/**
 * Classifies a Supabase / query error into a type string for logging.
 */
export function classifyError(error: unknown): string {
  if (isRLSError(error)) return 'rls_denial';
  const msg = typeof error === 'string' ? error : (error as { message?: string }).message ?? '';
  if (msg.includes('JWT')) return 'auth_expired';
  if (msg.includes('NetworkError') || msg.includes('Failed to fetch')) return 'network';
  if (msg.includes('timeout') || msg.includes('TIMEOUT')) return 'timeout';
  if (msg.includes('duplicate key') || msg.includes('unique constraint')) return 'duplicate';
  if (msg.includes('violates foreign key')) return 'foreign_key';
  return 'unknown';
}

/**
 * In-memory crash counter per page. Useful for circuit-breaker patterns
 * or dashboards that poll the singleton.
 */
const crashCounts = new Map<string, number>();

export function incrementCrashCount(page: string): number {
  const current = crashCounts.get(page) ?? 0;
  const next = current + 1;
  crashCounts.set(page, next);
  return next;
}

export function getCrashCount(page: string): number {
  return crashCounts.get(page) ?? 0;
}

export function getAllCrashCounts(): Record<string, number> {
  return Object.fromEntries(crashCounts);
}
